import { useEffect, useState } from 'react';
import { openExternalUrl } from '@/utils/url-utils';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/react';
import { z } from '@/lib/zod-config';
import { FileText, Trash2, Pencil, Copy, Download, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import HelpTooltip from '@/components/ui/help-tooltip';
import { useNotification } from '@/components/notifications';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';

import { EvalModel } from '@/types/evals-types';
import type { Eval } from '@/types/evals-types';
import { modelTypes, EVAL_NAME_MAX_LENGTH } from './base-eval-config';
import {
  deleteEvalV2,
  downloadEvalV2File,
  uploadEvalV2File,
} from '@/services/evaluation/eval-v2';
import { Dropzone, AcceptType } from '@/components/dropzone';
import EvalDescriptionPanel from './eval-description-panel';
import { useEvalTypeMetadata } from '@/hooks/queries/evaluation/use-get-eval-types';
import { PageTitle } from '@/components/page-title';

const EVAL_UPLOAD_MAX_SIZE_MB = 32;

const EVAL_DROPZONE_ACCEPT = {
  ...AcceptType.PDF,
  ...AcceptType.DOCUMENT,
  ...AcceptType.PRESENTATION,
  ...AcceptType.SPREADSHEET,
  ...AcceptType.MARKDOWN,
  ...AcceptType.TEXT,
  ...AcceptType.JSON,
  ...AcceptType.IMAGE,
  'text/html': ['.html'],
  'image/gif': ['.gif'],
};

const EVAL_ACCEPTED_TYPES_LABEL = [
  ...new Set(Object.values(EVAL_DROPZONE_ACCEPT).flat()),
].join(', ');

const formSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(
      EVAL_NAME_MAX_LENGTH,
      `Name must be ${EVAL_NAME_MAX_LENGTH} characters or less`,
    ),
  description: z.string().trim().min(1, 'Description is required'),
  judge_model: z.nativeEnum(EvalModel),
  reference_text: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

type ReferenceContentMode = 'text' | 'file';

function getInitialReferenceMode(evalData: Eval): ReferenceContentMode {
  const { s3Key, referenceText } = getExistingFileInfo(evalData);
  if (s3Key) return 'file';
  if (referenceText.trim()) return 'text';
  return 'text';
}

interface ReferenceOutputEvalConfigProps {
  evalData: Eval;
  onSave: (data: Partial<Eval>) => void;
  onDuplicate: () => void;
  onDelete?: (evalId: string) => void;
}

function getExistingFileInfo(evalData: Eval) {
  const cfg =
    (evalData?.eval_config as unknown as Record<string, unknown>) ?? {};
  const s3Key =
    typeof cfg.s3_key === 'string' && cfg.s3_key ? cfg.s3_key : null;
  const s3Bucket =
    typeof cfg.s3_bucket === 'string' && cfg.s3_bucket ? cfg.s3_bucket : null;
  const fileLabel = s3Key ? s3Key.split('/').pop() || s3Key : null;
  const referenceText =
    typeof cfg.reference_output === 'string' ? cfg.reference_output : '';
  return { s3Key, s3Bucket, fileLabel, referenceText };
}

function getDefaultValues(evalData: Eval): FormValues {
  const { referenceText } = getExistingFileInfo(evalData);
  return {
    name: evalData?.name || '',
    description: evalData?.description || '',
    judge_model:
      evalData?.model === EvalModel.GPT_4O
        ? EvalModel.GPT_4O_MINI
        : evalData?.model || EvalModel.CLAUDE_SONNET_46,
    reference_text: referenceText,
  };
}

export default function ReferenceOutputEvalConfig({
  evalData,
  onSave,
  onDuplicate,
  onDelete,
}: ReferenceOutputEvalConfigProps) {
  const notification = useNotification();
  const metadata = useEvalTypeMetadata('exemplar');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dropzoneKey, setDropzoneKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isExistingFileRemoved, setIsExistingFileRemoved] = useState(false);
  const [referenceMode, setReferenceMode] = useState<ReferenceContentMode>(() =>
    getInitialReferenceMode(evalData),
  );

  const isExisting = !evalData.is_new;
  const [isEditing, setIsEditing] = useState(!isExisting);

  const form = useForm<FormValues>({
    defaultValues: getDefaultValues(evalData),
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    form.reset(getDefaultValues(evalData));
    setSelectedFile(null);
    setIsExistingFileRemoved(false);
    setReferenceMode(getInitialReferenceMode(evalData));
    setDropzoneKey((k) => k + 1);
    setIsEditing(!!evalData.is_new);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evalData]);

  const handleDownload = async () => {
    if (!evalData.id) return;
    setIsDownloading(true);
    try {
      const { presigned_url } = await downloadEvalV2File(evalData.id);
      openExternalUrl(presigned_url);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to download file';
      notification.error(message);
      console.error('Error downloading eval file:', error);
      Sentry.captureException(error, {
        extra: { context: 'eval-file-download', evalId: evalData.id },
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setDropzoneKey((k) => k + 1);
  };

  const handleSubmit = async (data: FormValues) => {
    const { s3Key: existingS3Key, s3Bucket: existingS3Bucket } =
      getExistingFileInfo(evalData);

    const hasNewText = !!(data.reference_text && data.reference_text.trim());
    const hasNewFile = !!selectedFile;
    const hasExistingFile = !!existingS3Key && !isExistingFileRemoved;

    if (referenceMode === 'text' && !hasNewText) {
      notification.error('Paste reference text to continue.');
      return;
    }

    if (referenceMode === 'file' && !hasNewFile && !hasExistingFile) {
      notification.error('Upload a reference file to continue.');
      return;
    }

    setIsSaving(true);
    try {
      let evalConfig: Record<string, unknown> = {};

      if (referenceMode === 'text') {
        evalConfig = { reference_output: data.reference_text!.trim() };
      } else if (hasNewFile) {
        const upload = await uploadEvalV2File(selectedFile!);
        evalConfig = { s3_key: upload.s3_key, s3_bucket: upload.s3_bucket };
      } else {
        evalConfig = {
          s3_key: existingS3Key!,
          ...(existingS3Bucket ? { s3_bucket: existingS3Bucket } : {}),
        };
      }

      onSave({
        id: evalData.id,
        name: data.name,
        description: data.description,
        model: data.judge_model,
        type: 'exemplar',
        eval_config: evalConfig as unknown as Eval['eval_config'],
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to upload reference file';
      notification.error(message);
      console.error('Error saving reference output eval:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!evalData.id) return;
    setIsDeleteLoading(true);
    try {
      await deleteEvalV2(evalData.id);
      notification.success('Evaluation deleted');
      onDelete?.(evalData.id);
    } catch (e) {
      notification.error('Failed to delete evaluation');
      console.error('Delete eval error:', e);
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  const { fileLabel: existingFileLabel } = getExistingFileInfo(evalData);
  const showExistingFile =
    isExisting &&
    !!existingFileLabel &&
    !selectedFile &&
    !isExistingFileRemoved;

  const handleRemoveExistingFile = () => {
    setIsExistingFileRemoved(true);
    setSelectedFile(null);
    setDropzoneKey((k) => k + 1);
  };

  return (
    <Form {...form}>
      <div className='flex items-center justify-between mb-3'>
        <PageTitle title='Reference Output Evaluation' />
        {isExisting && !isEditing && (
          <div className='flex items-center gap-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={onDuplicate}
            >
              <Copy className='h-4 w-4' />
              Duplicate
            </Button>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => setIsEditing(true)}
            >
              <Pencil className='h-4 w-4' />
              Edit
            </Button>
          </div>
        )}
      </div>

      {metadata && <EvalDescriptionPanel metadata={metadata} />}

      <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-6'>
        <div className='space-y-4'>
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <div className='flex items-center gap-2'>
                  <FormLabel className='label-required'>
                    Evaluation Name
                  </FormLabel>
                  <HelpTooltip label='Evaluation name help'>
                    Give your evaluation a name for easy reference.
                  </HelpTooltip>
                </div>
                <FormControl>
                  <Input
                    {...field}
                    placeholder='e.g. quarterly report reference'
                    maxLength={EVAL_NAME_MAX_LENGTH}
                    disabled={!isEditing}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='description'
            render={({ field }) => (
              <FormItem>
                <div className='flex items-center gap-2'>
                  <FormLabel className='label-required'>Description</FormLabel>
                  <HelpTooltip label='Description help'>
                    Describe what this reference represents and how it should be
                    compared against workflow output.
                  </HelpTooltip>
                </div>
                <FormControl>
                  <Input
                    {...field}
                    placeholder='e.g. gold-standard exec summary used as the comparison reference'
                    disabled={!isEditing}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='judge_model'
            render={({ field }) => (
              <FormItem>
                <div className='flex items-center gap-2'>
                  <FormLabel className='label-required'>Judge Model</FormLabel>
                  <HelpTooltip label='Judge model help'>
                    Only multi-modal models (Claude and GPT-4o/5 families) are
                    supported for reference output evaluation, since the
                    reference may be a binary file.
                  </HelpTooltip>
                </div>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={!isEditing}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder='Select a judge model' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {modelTypes.map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='space-y-3'>
            <div className='flex items-center gap-2'>
              <Label className='text-sm font-medium'>Reference Content</Label>
              <HelpTooltip label='Reference content help'>
                Provide the gold-standard reference as text or a file (
                {EVAL_ACCEPTED_TYPES_LABEL}). Max file size:{' '}
                {EVAL_UPLOAD_MAX_SIZE_MB} MB.
              </HelpTooltip>
            </div>

            <Tabs
              value={referenceMode}
              onValueChange={(value) => {
                if (value === 'text' || value === 'file') {
                  setReferenceMode(value);
                }
              }}
              className='gap-3'
            >
              <TabsList>
                <TabsTrigger value='text' disabled={!isEditing}>
                  Text
                </TabsTrigger>
                <TabsTrigger value='file' disabled={!isEditing}>
                  File
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value='text'
                forceMount
                className='mt-0 data-[state=inactive]:hidden'
              >
                <FormField
                  control={form.control}
                  name='reference_text'
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          className='min-h-[140px] max-h-[400px] overflow-y-auto resize-y'
                          placeholder='Paste the reference output the workflow should be compared against...'
                          disabled={!isEditing}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent
                value='file'
                forceMount
                className='mt-0 space-y-2 data-[state=inactive]:hidden'
              >
                {showExistingFile && (
                  <div className='flex items-center gap-2 rounded border border-input bg-muted/30 px-3 py-2 text-sm'>
                    <FileText className='size-4 shrink-0 text-muted-foreground' />
                    <span className='truncate flex-1'>{existingFileLabel}</span>
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      onClick={handleDownload}
                      disabled={isDownloading}
                      title='Download file'
                      aria-label='Download file'
                    >
                      <Download className='size-4' />
                    </Button>
                    {isEditing && (
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        onClick={handleRemoveExistingFile}
                        title='Remove file'
                        aria-label='Remove file'
                        className='text-muted-foreground hover:text-destructive'
                      >
                        <X className='size-4' />
                      </Button>
                    )}
                    <span className='text-xs text-muted-foreground'>
                      current file
                    </span>
                  </div>
                )}

                <div
                  className={!isEditing ? 'pointer-events-none opacity-50' : ''}
                >
                  <Dropzone
                    key={dropzoneKey}
                    mode='manual'
                    onFilesChange={(files) => {
                      const file = files[0] ?? null;
                      setSelectedFile(file);
                      if (!file) clearFile();
                    }}
                    acceptType={EVAL_DROPZONE_ACCEPT}
                    maxSize={EVAL_UPLOAD_MAX_SIZE_MB}
                    multiple={false}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {isEditing && (
          <div className='flex justify-between items-center'>
            <div className='flex gap-2'>
              <Button type='submit' disabled={isSaving}>
                {isSaving ? 'Saving...' : isExisting ? 'Save' : 'Create'}
              </Button>
              {isExisting && (
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => {
                    form.reset(getDefaultValues(evalData));
                    setSelectedFile(null);
                    setIsExistingFileRemoved(false);
                    setReferenceMode(getInitialReferenceMode(evalData));
                    setDropzoneKey((k) => k + 1);
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
            {onDelete && isExisting && (
              <Button
                type='button'
                variant='ghost'
                className='text-muted-foreground hover:text-red-600'
                onClick={() => setIsDeleteModalOpen(true)}
              >
                <Trash2 className='h-4 w-4' />
                Delete Evaluation
              </Button>
            )}
          </div>
        )}
      </form>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        isLoading={isDeleteLoading}
        title='Delete Evaluation'
        description='Are you sure you want to delete this evaluation? All evaluation results and data will be permanently removed. This action cannot be undone.'
        buttonLabel='Delete Evaluation'
        handleDelete={handleDelete}
        handleCancel={() => setIsDeleteModalOpen(false)}
      />
    </Form>
  );
}
